import { ActionIcon, Avatar, ConfirmExternalLinkModal, Link, Text, View } from '@/components'
import { CommunityComment } from '@/types'
import { formatDistanceToNowCustom } from '@/utils'
import { useCallback, useMemo } from 'react'
import { FeedUsername } from '../atoms/Username'

type FeedCommentProps = {
  comment: CommunityComment
  onReport?: () => void
  onReply?: () => void
}

const TLDS = 'com|org|net|io|co|uk|app|dev|world|me|info|biz|edu|gov|co\\.uk|com\\.br'
const BARE_DOMAIN = `[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.(?:${TLDS})`
const URL_PATTERN = `\\b((?:https?:\\/\\/|www\\d{0,3}[.]|${BARE_DOMAIN})[^\\s()<>{}\\[\\]]*?(?=[.,;:!?]?(?:\\s|$)))`

const URL_REGEX_GLOBAL = new RegExp(URL_PATTERN, 'gi')
const URL_REGEX_CHECK = new RegExp(URL_PATTERN, 'i')
const MENTION_REGEX_GLOBAL = /@[A-Za-z0-9._]+(?: [A-Za-z0-9._]+)?/g
const MENTION_REGEX_CHECK = /^@[A-Za-z0-9._]+(?: [A-Za-z0-9._]+)?$/
const TOKEN_REGEX = new RegExp(`${URL_REGEX_GLOBAL.source}|${MENTION_REGEX_GLOBAL.source}`, 'gi')

const isInternalUrl = (url: string) => {
  try {
    const u = new URL(url, window.location.origin)
    return u.hostname === 'app.hurd.world' || u.origin === window.location.origin
  } catch {
    return false
  }
}

const normalizeUrl = (url: string): string => {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

export const FeedComment = (props: FeedCommentProps) => {
  const { comment, onReport, onReply } = props

  const handleLinkPress = useCallback(async (e: React.MouseEvent, internal: boolean, url: string) => {
    if (!internal) {
      e.preventDefault()

      const wantToOpen = await ConfirmExternalLinkModal.request()

      if (wantToOpen) {
        window.open(url, '_blank')
      }
    }
  }, [])

  const createdAt = useMemo(() => {
    return comment?.created_at ? formatDistanceToNowCustom(new Date(comment.created_at)) : null
  }, [comment?.created_at])

  const textElements = useMemo(() => {
    if (!comment?.text) return []

    const elements = []
    let lastIndex = 0

    Array.from(comment.text.matchAll(TOKEN_REGEX)).forEach((match, index) => {
      const [value] = match
      const start = match.index ?? 0

      if (start > lastIndex) {
        elements.push(comment.text.slice(lastIndex, start))
      }

      if (URL_REGEX_CHECK.test(value)) {
        const internal = isInternalUrl(value)
        const url = normalizeUrl(value)

        elements.push(
          <Link
            key={index}
            to={url}
            openNewTab={!internal}
            variants={['commentLink']}
            onClick={e => handleLinkPress(e, internal, url)}
          >
            {value}
          </Link>,
        )
      } else if (MENTION_REGEX_CHECK.test(value)) {
        elements.push(<Text key={`mention-${index}`} text={value} variants={['p3', 'bold', 'inline']} />)
      }

      lastIndex = start + value.length
    })

    if (lastIndex < comment.text.length) {
      elements.push(comment.text.slice(lastIndex))
    }

    return elements
  }, [comment?.text, handleLinkPress])

  if (!comment) return null

  return (
    <View variants={['gap:1.2', 'paddingBottom:1']}>
      {!!comment?.profile && (
        <Avatar
          debugName={`Feed comment avatar`}
          image={comment.profile?.avatar}
          name={comment.profile?.display_name}
          variants={['small']}
        />
      )}

      <View variants={['column', 'flex']}>
        {!!comment?.profile && <FeedUsername username={comment.profile.display_name} />}
        <Text variants={['p3', 'color:neutral9', 'breakLine']}>{textElements}</Text>
        <View variants={['row', 'gap:1']}>
          {createdAt && <Text text={createdAt} variants={['p4', 'color:neutral9', 'light']} />}
          {onReply && (
            <Text onPress={onReply} text={'Reply'} variants={['p4', 'color:neutral7', 'bold', 'cursor:pointer']} />
          )}
        </View>
      </View>

      {onReport && (
        <ActionIcon
          debugName={`Report feed comment ${comment.id}`}
          name='report'
          onPress={onReport}
          variants={['minimal', 'neutral5', 'alignSelfEnd']}
        />
      )}
    </View>
  )
}
